import * as React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useNavigate, useParams } from 'react-router-dom'; // Import for navigation

// Import step components
import { InitialStep } from './dialog-steps/InitialStep';
import { BuildScratchStep } from './dialog-steps/BuildScratchStep';
import { SelectConditionTypeStep } from './dialog-steps/SelectConditionTypeStep';
import { SelectActivityStep } from './dialog-steps/SelectActivityStep';

// Import the hook
import { useCreateSegmentDialog } from '../hooks/useCreateSegmentDialog';
// @ts-ignore
import { EventCohortInput, StepInput } from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';

// --- Refined Condition State Structure --- //

interface ConditionStateItemBase {
  clientId: string;
  displayName: string;
}

// Represents a single step within an Event Cohort, initially minimal
// Only requires eventName, other StepInput fields are optional here
type InitialStepState = Pick<StepInput, 'eventName'> & Partial<Omit<StepInput, 'eventName'>>;

// Represents an Event Cohort state item
interface EventConditionState extends ConditionStateItemBase {
  type: 'event';
  cohort: Omit<Partial<EventCohortInput>, 'steps'> & {
    steps: [InitialStepState]; // Define structure with one initial step state
  };
}

// Placeholder for User Cohort state item
// interface UserConditionState extends ConditionStateItemBase { ... }

// Combined state item type
export type ConditionStateItem = EventConditionState; // | UserConditionState

// Define step types

interface CreateSegmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSegmentDialog({ open, onOpenChange }: CreateSegmentDialogProps) {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Callback for successful segment creation
  const handleSegmentCreated = (newSegmentId: string) => {
    console.log(`Segment ${newSegmentId} created, navigating...`);
    onOpenChange(false); // Close the dialog
    void navigate(`/merchants/${slug!}/segments/builder/${newSegmentId}`);
    toast({
      title: 'Segment created',
      description: 'Your new segment has been created successfully.',
      variant: 'default'
    });
  };

  // --- Use the custom hook --- //
  const {
    step,
    // Get persisted state and setters
    segmentName,
    segmentDescription,
    setSegmentName,
    setSegmentDescription,
    conditions,
    // operators, // Not directly used by component UI
    handlers,
    createSegmentHandler,
    isCreatingSegment
  } = useCreateSegmentDialog(open, { onSegmentCreated: handleSegmentCreated });

  // --- Render Logic (Simpler, uses hook values/handlers) --- //
  const renderStep = () => {
    switch (step) {
      case 'buildScratch':
        return (
          <BuildScratchStep
            // Initialize form with persisted values
            defaultValues={{
              segmentName: segmentName,
              segmentDescription: segmentDescription
            }}
            // Pass setters to sync local changes back to hook
            onNameChange={setSegmentName}
            onDescriptionChange={setSegmentDescription}
            // @ts-ignore
            conditions={conditions as EventConditionState[]}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={createSegmentHandler}
            onAddCondition={handlers.handleAddCondition}
            onCancel={() => {
              onOpenChange(false);
            }}
            onDeleteCondition={handlers.handleDeleteCondition}
            isSubmitting={isCreatingSegment}
          />
        );
      case 'selectConditionType':
        return (
          <SelectConditionTypeStep
            onBack={handlers.handleGoBack}
            onSelectActivity={handlers.handleSelectActivity}
            onSelectCharacteristic={handlers.handleSelectCharacteristic}
          />
        );
      case 'selectActivity':
        return (
          <SelectActivityStep
            onBack={handlers.handleGoBack}
            onAddConditions={handlers.handleAddSelectedConditions}
          />
        );
      case 'initial':
      default:
        return (
          <InitialStep
            onBuildScratch={handlers.handleBuildScratch}
            onUseTemplate={handlers.handleUseTemplate}
            onUploadCsv={handlers.handleUploadCsv}
          />
        );
    }
  };

  // --- Dialog Width --- //
  const dialogWidthClass = React.useMemo(() => {
    switch (step) {
      case 'buildScratch':
        return 'sm:max-w-[650px]';
      default: {
        return 'sm:max-w-[450px]';
      }
    }
  }, [step]);

  // --- Final Return --- //
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* TODO: Add loading indicator based on isCreatingSegment? */}
      {/* TODO: Add error display based on createError? */}
      <DialogContent className={dialogWidthClass}>{renderStep()}</DialogContent>
    </Dialog>
  );
}
